import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

import { authService, type UserProfile } from '../services/authService';
import { resetAuthInterceptor } from '../services/api';

interface AuthContextType {
    token: string | null;
    user: UserProfile | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (token: string) => void;
    logout: () => void;
    updateUser: (profile: UserProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Guard against multiple simultaneous logout calls within the same
    // React render cycle (e.g. interceptor event + fetchUserProfile catch).
    const isLoggingOutRef = useRef(false);

    // Sync Sentry user context with auth state
    useEffect(() => {
        if (user) {
            Sentry.setUser({ id: user.id, email: user.email, username: user.name });
        } else {
            Sentry.setUser(null);
        }
    }, [user]);

    const fetchUserProfile = async () => {
        try {
            const profile = await authService.getUserProfile();
            setUser(profile);
        } catch (err: any) {
            if (err?.isAuthLogout) return;
        }
    };

    // Watch for infinite loading spinners on app init
    useEffect(() => {
        if (!isLoading) return;
        const timer = setTimeout(() => {
            Sentry.captureMessage("UI bloqueada en estado de carga: Inicio de App (AuthContext)", "warning");
        }, 10000);
        return () => clearTimeout(timer);
    }, [isLoading]);

    // Bootstrap: read the stored token once on mount
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            setToken(storedToken);
            fetchUserProfile().finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }
    }, []);

    // Listen for the interceptor's auth:logout event.
    // This replaces the old 'storage' event listener, which was fragile
    // for same-tab dispatches and could race with fetchUserProfile.
    useEffect(() => {
        const handleAuthLogout = () => {
            if (isLoggingOutRef.current) return; // already processing
            isLoggingOutRef.current = true;

            setToken(null);
            setUser(null);

            // Reset guard after the current micro-task / render cycle
            queueMicrotask(() => {
                isLoggingOutRef.current = false;
            });
        };

        window.addEventListener('auth:logout', handleAuthLogout);
        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, []);

    const login = async (newToken: string) => {
        // Reset the interceptor's deduplication flag so future 401s
        // after this login are handled correctly — even without a
        // full page reload (PWA / SPA scenario).
        resetAuthInterceptor();
        isLoggingOutRef.current = false;

        localStorage.setItem('token', newToken);
        setToken(newToken);
        await fetchUserProfile();
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const updateUser = (profile: UserProfile) => {
        setUser(profile);
    };

    return (
        <AuthContext.Provider
            value={{
                token,
                user,
                isAuthenticated: !!token,
                isLoading,
                login,
                logout,
                updateUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
